import { Badge, Popover, Flex, Image, Typography, Space } from "antd";
import { BaseMessage, Media } from "../../../interfaces/messages.type";
import {
  ChatText,
  DateText,
  DeleteText,
  FileText,
  NameTextChat,
} from "../../generic/Typography";
import { formatDateToTime } from "../../../utils/dateFormat";
import { DownloadOutlined, RotateLeftOutlined, RotateRightOutlined, SwapOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { HiDotsVertical } from "react-icons/hi";
import { FaRegCopy } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import type { MenuProps } from "antd";
import { Dropdown } from "antd";
import useMessageToast from "../../../hooks/error/errorMessage";
import Waveform from "./chat.audio";
import { CheckCircleTwoTone, LoadingOutlined } from '@ant-design/icons';
import { MdErrorOutline } from "react-icons/md";
import { IoReload } from "react-icons/io5";
import { memo, useCallback, useState } from "react";
import { DisplayMessage } from "../../../store/slices/messages.slice";
import { formatPhoneNumber } from "../../../utils/phoneLib";
import { ContactsByPhone, participantName } from "../../../utils/conversationDisplay";



const chatBoxStyle: React.CSSProperties = {
  borderRadius: "10px",
  padding: "10px",
};
const dotsWrapperStyle: React.CSSProperties = {
  height: "100%",
  top: "0px",
  right: "-16px",
  position: "absolute",
};
const dotsStyle: React.CSSProperties = { color: "gray", cursor: "pointer" };

const ChatCard = memo(({
  ffmpegRef,
  message,
  deleteMessage,
  sendMessage,
  contactsByPhone,
}: {
  ffmpegRef: any;
  message: DisplayMessage;
  deleteMessage: Function,
  sendMessage: Function;
  contactsByPhone?: ContactsByPhone;
}) => {

  const { contextHolder, success } = useMessageToast();

  const [current, setCurrent] = useState(0);
  const legs: BaseMessage[] = message.legs?.length ? message.legs : [message];
  const isInboundGroupMessage =
    message.direction === "inbound" &&
    (message.conversation_type === "group" ||
      (message.conversation_participants?.length ?? 0) > 1);
  const senderName =
    isInboundGroupMessage && contactsByPhone
      ? participantName(message.participant, contactsByPhone)
      : (formatPhoneNumber(message.participant) ?? message.participant);
  const legStatuses = legs.map((leg) => leg.status || "pending");
  const aggregateStatus =
    legStatuses.every((status) => status === "delivered")
      ? "delivered"
      : legStatuses.every((status) => status === "failed")
        ? "failed"
        : legStatuses.some((status) => status === "pending")
          ? "pending"
          : legStatuses.some((status) => status === "failed")
            ? "partial"
            : "sent";
  const deliveryDetailsTitle = (
    <Typography.Text style={{ fontSize: "12px", fontWeight: 600 }}>
      Delivery details
    </Typography.Text>
  );
  const statusDetails = (
    <Flex vertical gap={8} style={{ fontSize: "12px", minWidth: "220px" }}>
      {legs.map((leg, index) => {
        const status = leg.status || "pending";
        const error = leg.display_error || leg.failed_reason;

        return (
          <Flex vertical gap={2} key={leg.id || `${leg.participant}-${index}`}>
            <Flex align="center" gap={8}>
              {status === "failed" ? (
                <MdErrorOutline color="#ff4d4f" />
              ) : status === "pending" ? (
                <LoadingOutlined />
              ) : (
                <CheckCircleTwoTone
                  twoToneColor={status === "delivered" ? ['#333', '#f6ffed'] : "#0000ff"}
                />
              )}
              <Typography.Text style={{ fontSize: "12px" }}>{formatPhoneNumber(leg.participant)}</Typography.Text>
              <Typography.Text style={{ fontSize: "12px" }} type="secondary">{status}</Typography.Text>
            </Flex>
            {error && (
              <Typography.Text style={{ fontSize: "11px", paddingLeft: "24px" }} type="danger">
                {error}
              </Typography.Text>
            )}
          </Flex>
        );
      })}
    </Flex>
  );

  const chatMenu: MenuProps["items"] = [
    {
      key: "1",
      label: "Copy",
      icon: <FaRegCopy />,
      onClick: () => {
        navigator.clipboard.writeText(message.body);
        success("Copied");
      },
    },
    {
      key: "2",
      label: "Delete",
      icon: <MdDeleteOutline />,
      onClick: () => {
        legs.forEach((leg) => {
          if (leg.id) deleteMessage(leg.id);
        });
      },
    },
  ];

  const handleResendFailureMessage = useCallback(() => {
    if (message.offlineMessage) {
      sendMessage({
        text: message.body,
        from: message.did,
        ...(message.conversation_id
          ? { conversation_id: message.conversation_id }
          : {
              to:
                message.conversation_participants?.length === 1
                  ? message.conversation_participants[0]
                  : message.conversation_participants || [message.participant],
            }),
        media: message.media,
        clientId: message.clientId
      });
    }
  }, [message, sendMessage]);

  const onDownload = useCallback((media: Media[]) => {
    const { url, filename } = media[current];

    fetch(`${url}?not-from-cache-please`)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(blobUrl);
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error("Error downloading file:", error);
      });
  }, [current]);

  return (
    <Flex vertical={true}>
      {contextHolder}
      <DateText
        style={{
          textAlign: message.direction === "outbound" ? "left" : "right",
        }}
        text={formatDateToTime(new Date(message.timestamp))}
      />
      <Flex
        justify={message.direction === "outbound" ? "flex-start" : "flex-end"}
        style={{
          position: "relative",
          alignItems: "center",
          width: message.direction === "outbound" ? "fit-content" : "",
        }}
      >
        {message.direction === "inbound" && !message.deleted &&
          <Dropdown menu={{ items: chatMenu }} trigger={["click"]}>
            <HiDotsVertical style={dotsStyle} />
          </Dropdown>
        }
        <Flex
          gap={10}
          style={{
            ...chatBoxStyle,
            background:
              message.direction === "outbound"
                ? "rgb(223, 226, 232)"
                : "rgb(255, 255, 255)",
          }}
          vertical={message.direction === "inbound" ? true : false}
        >
          {message.body !== "" && !message.deleted && (
            <ChatText text={message.body} />
          )}
          {message.deleted && <DeleteText />}
          {!message.deleted &&
            message.media.some(
              (media: any) =>
                media.content_type === "image/jpeg" ||
                media.content_type === "image/png" ||
                media.content_type === "image/jpg" ||
                (media.content_type === "" && media.content &&
                  (media.content.match(/^data:(.*?);base64,/)[1] === "image/jpeg" ||
                    media.content.match(/^data:(.*?);base64,/)[1] === "image/png" ||
                    media.content.match(/^data:(.*?);base64,/)[1] === "image/jpg"))
            ) && (
              <Image.PreviewGroup
                preview={{
                  toolbarRender: (_, { transform: { scale }, actions: { onFlipY, onFlipX, onRotateLeft, onRotateRight, onZoomOut, onZoomIn } }) => (
                    <Space size={12} className="toolbar-wrapper">
                      <DownloadOutlined onClick={() => onDownload(message.media)} />
                      <SwapOutlined rotate={90} onClick={onFlipY} />
                      <SwapOutlined onClick={onFlipX} />
                      <RotateLeftOutlined onClick={onRotateLeft} />
                      <RotateRightOutlined onClick={onRotateRight} />
                      <ZoomOutOutlined disabled={scale === 1} onClick={onZoomOut} />
                      <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} />
                    </Space>
                  ),
                  onChange: (index) => {
                    setCurrent(index);
                  },
                }}
              >
                {message.media.map((media: Media, index) => {
                  if (media.content_type === "image/jpeg" || media.content_type === "image/png" || media.content_type === "image/jpg") {
                    return (
                      <Flex>
                        <Image
                          style={{ marginTop: "5px" }}
                          width={"200px"}
                          src={media.url ? media.url : media.content}
                          key={index}
                          fallback="data:image/png;base64,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"
                        />
                      </Flex>
                    );
                  } else {
                    return null;
                  }
                })}
              </Image.PreviewGroup>
            )}

          {!message.deleted &&
            message.media.some(
              (media) =>
                !media.content_type.includes("audio") &&
                !media.content_type.includes("image") &&
                media.content_type !== ""
            ) && (
              <Flex vertical={true}>
                {message.media.map((media) => {
                  return (
                    <Flex>
                      <FileText text={media.filename} />
                      <a href={media.url} download={media.url}>
                        <DownloadOutlined />
                      </a>
                    </Flex>
                  );
                })}
              </Flex>
            )}
          {!message.deleted &&
            message.media.some(
              (media: any) =>
                (media.content_type.includes("audio") &&
                  !media.content_type.includes("amr")) ||
                (media.content_type === "" && media.content &&
                  media.content.match(/^data:(.*?);base64,/)[1].includes("audio"))
            ) && (
              <>
                {message.media.map((media) => {
                  return (
                    <Waveform
                      ffmpegRef={ffmpegRef}
                      content_type={media.content_type}
                      url={media.url ? media.url : media.content as string}
                      direction={message.direction}
                    />
                  );
                })}
              </>
            )}
          {!message.deleted &&
            message.media.some((media: any) =>
              media.content_type.includes("audio/amr")
            ) && (
              <>
                {message.media.map((media) => {
                  return (
                    <Waveform
                      ffmpegRef={ffmpegRef}
                      content_type={media.content_type}
                      url={media.url ? media.url : media.content as string}
                      direction={message.direction}
                    />
                  );
                })}
              </>
            )}

          {message.direction === "outbound" &&
            !message.deleted &&
            aggregateStatus === "pending" && !message.offlineMessage &&
            <Popover content={statusDetails} title={deliveryDetailsTitle} trigger="hover">
              <span style={{ cursor: "pointer", display: "inline-flex" }}><LoadingOutlined /></span>
            </Popover>}

          {message.direction === "outbound" &&
            !message.deleted &&
            aggregateStatus === "pending" && message.offlineMessage &&
            <Popover content={statusDetails} title={deliveryDetailsTitle} trigger="hover">
              <IoReload onClick={() => handleResendFailureMessage()} style={{ margin: "auto", cursor: "pointer" }} color="blue" />
            </Popover>}

          {message.direction === "outbound" &&
            !message.deleted &&
            (aggregateStatus === "sent" || aggregateStatus === "delivered") &&
            <Popover content={statusDetails} title={deliveryDetailsTitle} trigger="hover">
              <span style={{ cursor: "pointer", display: "inline-flex" }}>
                <CheckCircleTwoTone twoToneColor={aggregateStatus === "delivered" ? ['#333', '#f6ffed'] : "#0000ff"} />
              </span>
            </Popover>
          }

          {message.direction === "outbound" &&
            !message.deleted &&
            aggregateStatus === "partial" &&
            <Popover content={statusDetails} title={deliveryDetailsTitle} trigger="hover">
              <Badge color="#ff4d4f" dot offset={[-5, 3]}>
                <span aria-label="Mixed delivery status" style={{ cursor: "pointer", display: "inline-flex" }}>
                  <CheckCircleTwoTone
                    twoToneColor={legStatuses.some((status) => status === "delivered") ? ['#333', '#f6ffed'] : "#0000ff"}
                  />
                </span>
              </Badge>
            </Popover>
          }

          {
            message.direction === "outbound" &&
            !message.deleted &&
            aggregateStatus === "failed" &&
            <Popover content={statusDetails} title={deliveryDetailsTitle} trigger="hover">
              <span style={{ cursor: "pointer", display: "inline-flex" }}>
                <MdErrorOutline color="red" style={{ height: "100%" }} />
              </span>
            </Popover>
          }



        </Flex>
        {message.direction === "outbound" && !message.deleted && (
          <Flex style={dotsWrapperStyle} justify="center" align="center">
            <Dropdown menu={{ items: chatMenu }} trigger={["click"]}>
              <HiDotsVertical style={dotsStyle} />
            </Dropdown>
          </Flex>
        )}
      </Flex>
      {message.direction === "outbound" &&
        !message.deleted &&
        (aggregateStatus === "sent" || aggregateStatus === "delivered" || aggregateStatus === "partial") &&
        message.first_name &&
        message.last_name &&
        <NameTextChat text={`${message.first_name} ${message.last_name}`} />
      }
      {isInboundGroupMessage && !message.deleted && (
        <Flex justify="flex-end">
          <NameTextChat text={senderName} />
        </Flex>
      )}
      {message.direction === "outbound" &&
        !message.deleted &&
        aggregateStatus === "pending" && message.offlineMessage && <Typography.Text style={{ color: "red", fontSize: "11px" }}>{"Connection error."}</Typography.Text>}
    </Flex>
  );
});

export default ChatCard;
